import { Component, EventEmitter, Output } from '@angular/core'

import { ApiPaymentBatchService, ParsedBatchRow } from '@modules/private/payment/data/services/api/api-payment-batch.service'
import { BatchMode, PaymentBatchStateService } from '@modules/private/payment/data/services/view/payment-batch-state.service'

/**
 * Paso 2 · La revisión, y el alta. **Primer punto de no retorno.**
 *
 * Lo que el archivo trae, fila por fila, antes de que exista un solo pago. Y el interruptor que
 * decide de qué tamaño es el viaje (D6): solo darlos de alta, o darlos de alta y aplicarlos.
 *
 * El interruptor vive aquí y no en el paso del archivo a propósito: la decisión se toma sabiendo qué
 * trae la hoja, y aquí es donde se sabe.
 *
 * ## Por qué el alta ocurre aquí y no en una pantalla propia
 *
 * La tenía: un paso entero que repetía en forma de lista las mismas cuatro cifras que esta pantalla
 * ya enseña en tarjetas —listas, monto, duplicados, filas fuera—, y cuya única aportación real era
 * una casilla de confirmación. Volver a leer lo mismo escrito distinto no es revisar dos veces: es
 * un paso más entre el operador y su trabajo.
 *
 * Lo que sí valía de aquel paso se conserva y baja aquí: **el aviso de que esto no se deshace** y la
 * casilla, pegados al botón que lo hace. La confirmación no se quitó — se puso donde están las cifras
 * que confirma.
 */
@Component({
	selector: 'app-step2-revision',
	templateUrl: './step2-revision.component.html',
	styleUrls: ['./step2-revision.component.scss'],
})
export class Step2RevisionComponent {
	@Output() onDone = new EventEmitter<void>()
	@Output() onBack = new EventEmitter<void>()
	@Output() onModeChange = new EventEmitter<void>()
	/** Cerrar el asistente desde la barra de acciones, no solo desde la X de la cabecera. */
	@Output() onExit = new EventEmitter<void>()
	/**
	 * Los pagos ya existen. Se avisa en cuanto ocurre y no al avanzar de paso: la dirección del lote
	 * tiene que estar en la barra desde el instante en que hay algo que recuperar.
	 */
	@Output() onRegistered = new EventEmitter<void>()

	/** Enseñar solo las filas con problema. Con cuarenta filas y tres malas, es lo que se busca. */
	soloProblemas = false

	/** La casilla del punto de no retorno. Arranca apagada en cada visita, a propósito. */
	confirmado = false
	registrando = false
	error = ''

	readonly modos: { label: string; value: BatchMode; hint: string }[] = [
		{ label: 'Solo dar de alta', value: 'SOLO_ALTA', hint: 'Los pagos quedan registrados y sin aplicar. Se aplican después, uno a uno, desde el arrendamiento.' },
		{ label: 'Dar de alta y aplicar', value: 'ALTA_Y_APLICA', hint: 'Además de registrarlos, eliges a qué arrendamiento va cada uno y revisas el impacto antes de aplicar.' },
	]

	constructor(
		public readonly state: PaymentBatchStateService,
		private readonly api: ApiPaymentBatchService
	) {}

	get rows(): ParsedBatchRow[] {
		const todas = this.state.parse?.rows ?? []

		return this.soloProblemas ? todas.filter((r) => r.status !== 'LISTA') : todas
	}

	get hayProblemas(): boolean {
		return (this.state.parse?.errorCount ?? 0) + (this.state.parse?.duplicateCount ?? 0) > 0
	}

	/**
	 * El archivo ya se cargó antes: el alta lo va a rechazar entero.
	 *
	 * Se dice aquí y se bloquea el paso, en vez de dejar que el operador pulse «Registrar» y reciba el
	 * rechazo del otro lado.
	 */
	get yaCargado(): boolean {
		return (this.state.parse?.alreadyLoaded ?? 0) > 0
	}

	/** Hay algo que registrar y nada que lo impida. La casilla se pide aparte. */
	get puedeSeguir(): boolean {
		return !this.yaCargado && (this.state.parse?.readyCount ?? 0) > 0
	}

	get puedeRegistrar(): boolean {
		return this.puedeSeguir && this.confirmado && !this.registrando
	}

	/**
	 * Crea los pagos. **Después de esto existen.**
	 *
	 * Se manda el ARCHIVO otra vez y no las filas que devolvió la lectura: el alta vuelve a resolver
	 * todo con su propio código y la huella del lote se calcula sobre el mismo contenido, que es lo
	 * que sostiene el guardia anti-recarga.
	 */
	registrar(): void {
		if (!this.puedeRegistrar || !this.state.file) return

		this.registrando = true
		this.error = ''

		this.api.register(this.state.file).subscribe((r) => {
			this.registrando = false
			if (r.error || !r.data) {
				this.error = r.msg || 'No se pudieron registrar los pagos.'
				return
			}

			this.state.setRegistered(r.data)
			this.onRegistered.emit()
			//* Avanzar al informe del alta: es la MISMA pantalla que ya existía, solo que ahora es lo
			//* único que vive en ese paso.
			this.onDone.emit()
		})
	}

	cambiarModo(modo: BatchMode): void {
		this.state.mode = modo
		this.onModeChange.emit()
	}

	/** Cuántos arrendamientos activos tiene el cliente de la fila: es lo que decide si habrá que elegir. */
	destinos(row: ParsedBatchRow): string {
		if (row.status !== 'LISTA') return '—'
		if (!row.activeLeases.length) return 'Sin arrendamiento activo'
		if (row.activeLeases.length === 1) return row.activeLeases[0].folio

		return `${row.activeLeases.length} — hay que elegir`
	}

	claseFila(row: ParsedBatchRow): string {
		if (row.status === 'ERROR') return 'is-error'
		if (row.status === 'POSIBLE_DUPLICADO') return 'is-warn'

		return ''
	}
}
